import { usePostProjectQuery } from "@smp/graphql";
import { UiDialogResponsive, UiSpinner } from "@smp/ui";
import { queryGetMainKey } from "@smp/utils";
import clsx from "clsx";
import { FC, useCallback, useLayoutEffect, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { useEffectOnce, useUpdateEffect } from "react-use";

import { PolotnoEditor } from "@/components/features/polotno_editor";
import { useContextApp } from "@/screens/app/context/app";
import { yaReachGoal } from "@/utils/ya_reach_goal";

import inlineCss from "./dialog_pc_v1.inject.module.css?inline";
import classes from "./dialog_pc_v1.module.css";
import { IDialogPcV1Props } from "./dialog_pc_v1.types";

export const DialogPcV1: FC<IDialogPcV1Props> = ({
  isOpen,
  onClose,
  groupId,
  postProjectId,
  pageConfigId,
  scheduleTime,
}) => {
  const queryClient = useQueryClient();

  const [isLoaded, setLoaded] = useState(false);
  const [refIframe, setRefIframe] = useState<HTMLIFrameElement | null>(null);
  const [isPolotnoEditorOpen, setPolotnoEditorOpen] = useState(false);

  const {
    state: { organizationId },
  } = useContextApp();

  const src = useMemo(() => {
    const params = new URLSearchParams();

    if (organizationId) {
      params.append("organizationContext", String(organizationId));
    }

    if (groupId !== undefined) {
      params.append("groupId", String(groupId));
    }

    if (postProjectId !== undefined) {
      params.append("postProjectId", String(postProjectId));
    }

    if (pageConfigId !== undefined) {
      params.append("pageId", String(pageConfigId));
    }

    if (scheduleTime !== undefined) {
      params.append("scheduleTime", String(scheduleTime));
    }

    return `/iframe/app/#/postproject?${params.toString()}`;
  }, [groupId, organizationId, pageConfigId, postProjectId, scheduleTime]);

  const handleIframeLoaded = useCallback(() => {
    if (!refIframe) {
      return;
    }

    setLoaded(true);
  }, [refIframe]);

  const handleIframeMessage = useCallback(
    (event: MessageEvent) => {
      if (!isOpen) {
        return;
      }

      switch (event.data.event) {
        case "polotno_editor_open":
          setPolotnoEditorOpen(true);
          break;

        case "posts_scheduled":
          if (event.data.data.selectedMediums?.length) {
            queryClient.invalidateQueries(queryGetMainKey(usePostProjectQuery));

            event.data.data.selectedMediums.forEach((medium: string) => {
              yaReachGoal("post_scheduled_old_form", { medium: medium });
            });
          }
          break;

        case "post_project_form_dismissed":
          onClose();
          break;
      }
    },
    [isOpen, onClose, queryClient],
  );

  const handleClosePolotnoEditor = useCallback(() => {
    setPolotnoEditorOpen(false);
  }, []);

  const handelCreateOrUpdatePolotno = useCallback(
    (data: Blob | Blob[]) => {
      setPolotnoEditorOpen(false);

      const blobs = Array.isArray(data) ? data : [data];

      blobs.forEach((blob) => {
        refIframe?.contentWindow?.postMessage({
          event: "polotno_editor_import",
          payload: blob,
        });
      });
    },
    [refIframe?.contentWindow],
  );

  useUpdateEffect(() => {
    if (!isOpen) {
      setLoaded(false);
    }
  }, [isOpen]);

  /**
   * inject css
   */
  useLayoutEffect(() => {
    if (!refIframe) {
      return;
    }

    const doc = refIframe?.contentDocument;

    if (doc) {
      doc
        ?.querySelectorAll("[data-id=injected-css]")
        ?.forEach((el) => el.remove());

      const cssStyle = document.createElement("style");
      cssStyle.setAttribute("data-id", "injected-css");

      cssStyle.innerHTML = inlineCss;
      doc.head.appendChild(cssStyle);
    }
  }, [isLoaded, refIframe]);

  /**
   * message handlers
   */
  useEffectOnce(() => {
    window.addEventListener("message", handleIframeMessage);

    return () => {
      window.removeEventListener("message", handleIframeMessage);
    };
  });

  return (
    <>
      <UiDialogResponsive
        open={isOpen}
        onClose={onClose}
        isButtonCloseHidden={true}
        fullWidth
        classes={{
          paper: classes.paper,
        }}
      >
        <iframe
          className={clsx(classes.iframe, isLoaded && classes.iframe_loaded)}
          src={src}
          ref={setRefIframe}
          onLoad={handleIframeLoaded}
          title="postproject"
        />

        {!isLoaded && <UiSpinner className={classes.spinner} />}
      </UiDialogResponsive>

      {isPolotnoEditorOpen && (
        <PolotnoEditor
          onClose={handleClosePolotnoEditor}
          onExport={handelCreateOrUpdatePolotno}
          isRoutingDisabled={true}
          reference="pc"
        />
      )}
    </>
  );
};
